// Components
import { Textarea, Select, Stack, Skeleton } from "@mantine/core";
import { FormActions } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import { CLINICAL_IMPRESSION_TYPE_SELECT_OPTIONS } from "@benchmark/core/constants";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useMedplumProfile, useResource } from "@medplum/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";

// Types
import type {
  ClinicalImpression,
  Practitioner,
  Patient,
} from "@medplum/fhirtypes";
import type { ClinicalImpressionFormData } from "@benchmark/core/types";

// Utils
import { clinicalImpressionToForm } from "@benchmark/core/adapters";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createClinicalImpressionFacade } from "@/facades/clinical-impression.facade";

/**
 * FHIR R4-compliant form for ClinicalImpression resources.
 * @agentic connections
 */

/** Converts ISO string to datetime-local input format. */
const formatDateTimeForInput = (isoString: string): string => {
  // Convert ISO string (UTC) to local datetime-local format
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/** Converts datetime-local input to ISO string (UTC). */
const formatInputForDateTime = (inputValue: string): string => {
  // Convert datetime-local input to ISO string (UTC)
  const localDate = new Date(inputValue);
  return localDate.toISOString();
};

const _getCurrentLocalDateTime = (): string => {
  // Get current time in local timezone for datetime-local input
  const now = new Date();
  return formatDateTimeForInput(now.toISOString());
};

/** Skeleton loading component for the clinical impression form. */
const ClinicalImpressionFormSkeleton = () => (
  <Stack gap="md">
    <Skeleton height={32} width="40%" /> {/* Title */}
    <Stack gap="xs">
      <Skeleton height={14} width="30%" /> {/* Label */}
      <Skeleton height={36} /> {/* Select */}
    </Stack>
    <Stack gap="xs">
      <Skeleton height={14} width="20%" /> {/* Label */}
      <Skeleton height={36} /> {/* DateTime input */}
    </Stack>
    <Stack gap="xs">
      <Skeleton height={14} width="25%" /> {/* Label */}
      <Skeleton height={120} /> {/* Textarea */}
    </Stack>
    <Skeleton height={44} width="200px" /> {/* Submit button */}
  </Stack>
);

/**
 * Props for the ClinicalImpressionForm component.
 * @agentic connections
 */
type ClinicalImpressionFormProps = {
  patientId?: string;
  clinicalImpression?: ClinicalImpression;
  onSuccess?: (clinicalImpressionId: string) => void;
  onCancel?: () => void;
};

/**
 * Form for creating and editing FHIR ClinicalImpression (clinical notes).
 * @agentic connections
 */
export const ClinicalImpressionForm = ({
  patientId: propPatientId,
  clinicalImpression: propClinicalImpression,
  onSuccess,
  onCancel,
}: ClinicalImpressionFormProps = {}) => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const clinicalImpressionFacade = createClinicalImpressionFacade(medplum);
  const profile = useMedplumProfile() as Practitioner;
  const { patientId: urlPatientId, clinicalImpressionId } = useStableParams();

  // Use prop patientId if provided, otherwise fall back to URL
  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  // Use prop clinicalImpression if provided, otherwise fetch from URL
  const fetchedClinicalImpression = useResource(
    clinicalImpressionId
      ? { reference: `ClinicalImpression/${clinicalImpressionId}` }
      : undefined,
  ) as ClinicalImpression | undefined;
  const existingClinicalImpression =
    propClinicalImpression || fetchedClinicalImpression;

  const form = useForm<ClinicalImpressionFormData>({
    initialValues: {
      "impression-type": "",
      effective: "",
      summary: "",
    },
    validate: {
      "impression-type": (value) => (!value ? "Note type is required" : null),
      effective: (value) => (!value ? "Date is required" : null),
      summary: (value) => (!value ? "Summary is required" : null),
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved note changes. Are you sure you want to close?",
  });

  const [isLoading, setIsLoading] = useState(true);

  // Use shared form submission handling
  const { isSubmitting, createSubmissionHandler } = useFormSubmission();

  // Load initial data when clinical impression or patient changes
  useEffect(() => {
    async function loadInitialData() {
      if (!patient) {
        return;
      }

      try {
        setIsLoading(true);

        if (!existingClinicalImpression) {
          // No existing clinical impression - create empty form data with current time
          const now = new Date().toISOString();
          const emptyData: ClinicalImpressionFormData = {
            "impression-type": "",
            effective: now,
            summary: "",
          };
          form.reset();
          form.setValues(emptyData);
        } else {
          // Existing clinical impression - load form data
          const data = clinicalImpressionToForm(existingClinicalImpression);
          form.reset();
          form.setValues(data);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
        // Fallback to empty form with current time
        const now = new Date().toISOString();
        form.reset();
        form.setValues({
          "impression-type": "",
          effective: now,
          summary: "",
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingClinicalImpression, patient]);

  const handleSubmit = useCallback(
    async (formData: ClinicalImpressionFormData) => {
      if (!profile || !patient) {
        showErrorNotification(
          "Missing required data. Please refresh the page and try again.",
        );
        return;
      }

      const createdOrUpdatedClinicalImpression =
        await clinicalImpressionFacade.upsert({
          formData,
          patient,
          encounter: undefined, // No encounter for standalone clinical impressions
          existingClinicalImpression,
        });

      showSuccessNotification(
        `Note ${existingClinicalImpression ? "updated" : "created"} successfully.`,
      );

      markAsClean();

      if (onSuccess) {
        onSuccess(createdOrUpdatedClinicalImpression?.id || "");
      } else {
        navigate(`/Patient/${patient.id}/Notes`)?.catch(console.error);
      }
    },
    [
      profile,
      patient,
      clinicalImpressionFacade,
      navigate,
      existingClinicalImpression,
      onSuccess,
    ],
  );

  // Show loading while fetching data
  if (!patient || !profile) {
    return <ClinicalImpressionFormSkeleton />;
  }

  // Show skeleton while loading existing clinical impression data
  if (isLoading) {
    return <ClinicalImpressionFormSkeleton />;
  }

  // Impression type options from custom ValueSet
  const impressionTypeOptions = CLINICAL_IMPRESSION_TYPE_SELECT_OPTIONS;

  return (
    <form onSubmit={form.onSubmit(createSubmissionHandler(handleSubmit))}>
      <Stack gap="md">
        <Select
          label="Note Type"
          placeholder="Select note type"
          data={impressionTypeOptions}
          required
          searchable
          clearable
          disabled={!!existingClinicalImpression}
          {...form.getInputProps("impression-type")}
        />

        <TextInput
          label="Date"
          type="datetime-local"
          required
          disabled={!!existingClinicalImpression}
          value={
            form.values.effective
              ? formatDateTimeForInput(form.values.effective)
              : ""
          }
          onChange={(event) => {
            const inputValue = event.currentTarget.value;
            if (inputValue) {
              form.setFieldValue(
                "effective",
                formatInputForDateTime(inputValue),
              );
            } else {
              form.setFieldValue("effective", "");
            }
          }}
          error={form.errors.effective}
        />

        <Textarea
          label="Summary"
          placeholder="Enter note summary"
          required
          minRows={4}
          {...form.getInputProps("summary")}
        />

        <FormActions
          isSubmitting={isSubmitting}
          resourceType="Note"
          isEditing={!!existingClinicalImpression}
          showCancel={true}
          onCancel={onCancel}
        />
      </Stack>
      <ConfirmationModal />
    </form>
  );
};
